import { defineStore } from "pinia";
import { computed, ref } from "vue";
import Config from "@/common/config";
import { encryptStorage } from "../utils/storage";
import { etCoreAuthAPI } from "@/api/etcore/etCoreAuth";
import type { EtCoreTokenResponse } from "@/api/etcore/etCoreAuth";

const REFRESH_SAFETY_WINDOW_IN_SECONDS = 30;

type EtCoreAuthFailureReason =
  "invalid_credentials" | "refresh_failed" | "network_error" | "unknown";

function getExpiresAt(expiresInSeconds: number): number {
  return Date.now() + expiresInSeconds * 1000;
}

function isExpiredOrAlmostExpired(
  expiresAt: number | undefined,
  safetyWindowInSeconds = REFRESH_SAFETY_WINDOW_IN_SECONDS,
): boolean {
  if (expiresAt === undefined) {
    return true;
  }

  return Date.now() >= expiresAt - safetyWindowInSeconds * 1000;
}

export const useEtCoreAuthStore = defineStore(
  "etCoreAuth",
  () => {
    const accessToken = ref<string>();
    const refreshToken = ref<string>();
    const accessTokenExpiresAt = ref<number>();
    const refreshTokenExpiresAt = ref<number>();
    const initialized = ref(false);
    const loginError = ref<unknown>();
    const lastFailureReason = ref<EtCoreAuthFailureReason>();

    let refreshPromise: Promise<string | undefined> | undefined;

    const authenticated = computed(() => accessToken.value !== undefined);

    function setTokenResponse(tokenResponse: EtCoreTokenResponse): void {
      accessToken.value = tokenResponse.access_token;
      refreshToken.value = tokenResponse.refresh_token;
      accessTokenExpiresAt.value = getExpiresAt(tokenResponse.expires_in);
      refreshTokenExpiresAt.value = getExpiresAt(
        tokenResponse.refresh_expires_in,
      );

      loginError.value = undefined;
      lastFailureReason.value = undefined;
      initialized.value = true;
    }

    function clear(): void {
      accessToken.value = undefined;
      refreshToken.value = undefined;
      accessTokenExpiresAt.value = undefined;
      refreshTokenExpiresAt.value = undefined;
      initialized.value = false;
      loginError.value = undefined;
      lastFailureReason.value = undefined;
    }

    async function loginWithPassword(
      username: string,
      password: string,
    ): Promise<boolean> {
      try {
        const tokenResponse = await etCoreAuthAPI.loginWithPassword(
          username,
          password,
        );

        setTokenResponse(tokenResponse);
        return true;
      } catch (error) {
        clear();
        loginError.value = error;
        lastFailureReason.value = "invalid_credentials";

        return false;
      }
    }

    async function refresh(): Promise<string | undefined> {
      if (refreshPromise !== undefined) {
        return refreshPromise;
      }

      if (
        refreshToken.value === undefined ||
        isExpiredOrAlmostExpired(refreshTokenExpiresAt.value, 0)
      ) {
        clear();
        lastFailureReason.value = "refresh_failed";
        return undefined;
      }

      refreshPromise = etCoreAuthAPI
        .refreshToken(refreshToken.value)
        .then((tokenResponse) => {
          setTokenResponse(tokenResponse);
          return tokenResponse.access_token;
        })
        .catch((error) => {
          clear();
          loginError.value = error;
          lastFailureReason.value = "refresh_failed";
          return undefined;
        })
        .finally(() => {
          refreshPromise = undefined;
        });

      return refreshPromise;
    }

    async function restoreFromPersistedRefreshToken(): Promise<boolean> {
      if (
        refreshToken.value === undefined ||
        isExpiredOrAlmostExpired(refreshTokenExpiresAt.value, 0)
      ) {
        clear();
        initialized.value = true;
        return false;
      }

      const nextAccessToken = await refresh();

      initialized.value = true;
      return nextAccessToken !== undefined;
    }

    async function getValidAccessToken(): Promise<string | undefined> {
      if (accessToken.value === undefined) {
        const restored = await restoreFromPersistedRefreshToken();

        if (!restored) {
          return undefined;
        }
      }

      if (!isExpiredOrAlmostExpired(accessTokenExpiresAt.value)) {
        return accessToken.value;
      }

      return refresh();
    }

    async function ensureInitialized(): Promise<boolean> {
      const token = await getValidAccessToken();
      initialized.value = true;

      return token !== undefined;
    }

    async function logout(): Promise<void> {
      const tokenToRevoke = refreshToken.value;

      clear();

      if (tokenToRevoke === undefined) {
        return;
      }

      try {
        await etCoreAuthAPI.logout(tokenToRevoke);
      } catch (error) {
        console.warn("ET Core Keycloak logout failed", error);
      }
    }

    return {
      accessToken,
      refreshToken,
      accessTokenExpiresAt,
      refreshTokenExpiresAt,
      initialized,
      authenticated,
      loginError,
      lastFailureReason,
      loginWithPassword,
      ensureInitialized,
      getValidAccessToken,
      refresh,
      restoreFromPersistedRefreshToken,
      clear,
      logout,
    };
  },
  {
    // @ts-ignore types not compatible, but works
    persist: {
      storage: encryptStorage,
      paths: ["refreshToken", "refreshTokenExpiresAt"],
      key: Config.etCoreAuthPersistKey,
      afterRestore: (context) => {
        const store = context.store as ReturnType<typeof useEtCoreAuthStore>;

        void store.ensureInitialized();
      },
    },
  },
);
